import { cur } from '@/lib/currency';
import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { ReportHeader } from '@/components/reports/ReportUI';
import { ReportFilterBar, useReportFilters } from '@/components/reports/ReportFilterBar';
import { ExportTrio } from '@/components/accounting/ExportTrio';
import { ShoppingCart } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import api from '@/lib/api';
import { toast } from 'sonner';
import { SSoTCompanyInfoBanner } from '@/components/common/SSoTCompanyInfoBanner';

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#8b5cf6', '#ef4444', '#06b6d4'];

export default function SalesSummaryReportPage() {
  const [data, setData] = useState(null);
  const f = useReportFilters({ withUnassigned: true });
  const dateFilter = f.range;
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFilter.start) params.start_date = dateFilter.start;
      if (dateFilter.end) params.end_date = dateFilter.end;
      if (f.branchIds.length) params.branch_ids = f.branchIds.join(',');
      const res = await api.get('/accounting/sales-summary-report', { params });
      setData(res.data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [f.params]);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="sales-summary-report-page">
        <ReportHeader icon={ShoppingCart} title="Sales Summary" subtitle="Aggregated sales by branch, payment mode and type" testId="ssum-header" />
        <ReportFilterBar testId="ssum-filter" branches={f.branches} branchIds={f.branchIds} onBranchIds={f.setBranchIds}
          range={f.range} onRange={f.setRange}>
          <ExportTrio spec={() => ({ title: 'Sales Summary', subtitle: `${f.range.start} → ${f.range.end} · ${f.branchLabel}`, filename: 'sales_summary', rows: data?.by_branch || [], cols: [{ key: 'name', label: 'Branch' }, { key: 'total', label: 'Total Sales', numeric: true }] })} disabled={!data} testId="ssum-export" />
        </ReportFilterBar>

        {/* iter690 — SSoT banner: every export carries the centrally-
            stored company VAT / CR / address — never re-typed. */}
        <SSoTCompanyInfoBanner scopeLabel="Report header" />

        {data && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Card className="border-success/30 bg-success/10"><CardContent className="pt-5"><p className="text-[10px] uppercase tracking-wider text-success font-semibold">Total Sales</p><p className="text-2xl font-bold text-success">{cur()} {data.total_sales?.toLocaleString()}</p></CardContent></Card>
              <Card><CardContent className="pt-5"><p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Cash</p><p className="text-2xl font-bold text-success">{cur()} {data.by_payment_mode?.cash?.toLocaleString()}</p></CardContent></Card>
              <Card><CardContent className="pt-5"><p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Bank</p><p className="text-2xl font-bold text-info">{cur()} {data.by_payment_mode?.bank?.toLocaleString()}</p></CardContent></Card>
              <Card><CardContent className="pt-5"><p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Credit</p><p className="text-2xl font-bold text-warning">{cur()} {data.by_payment_mode?.credit?.toLocaleString()}</p></CardContent></Card>
            </div>

            {data.by_branch?.length > 0 && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card><CardContent className="pt-5">
                  <h3 className="font-semibold mb-4">Sales by Branch</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={data.by_branch}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v) => `${cur()} ${Number(v).toLocaleString()}`} />
                      <Bar dataKey="total" fill="#10b981" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent></Card>
                <Card><CardContent className="pt-5">
                  <h3 className="font-semibold mb-4">Branch Distribution</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie data={data.by_branch} dataKey="total" nameKey="name" cx="50%" cy="50%" outerRadius={100} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                        {data.by_branch.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v) => `${cur()} ${Number(v).toLocaleString()}`} />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent></Card>
              </div>
            )}

            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 border-b"><tr>
                      <th className="text-left px-4 py-3 font-medium text-muted-foreground">Branch</th>
                      <th className="text-right px-4 py-3 font-medium text-muted-foreground">Total Sales</th>
                      <th className="text-right px-4 py-3 font-medium text-muted-foreground">% Share</th>
                    </tr></thead>
                    <tbody>
                      {data.by_branch?.map((b, i) => (
                        <tr key={i} className="border-b hover:bg-muted/50">
                          <td className="px-4 py-3 font-medium">{b.name}</td>
                          <td className="px-4 py-3 text-right font-bold text-success">{cur()} {b.total?.toLocaleString()}</td>
                          <td className="px-4 py-3 text-right text-muted-foreground">{data.total_sales > 0 ? ((b.total / data.total_sales) * 100).toFixed(1) : 0}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        )}
        {loading && <div className="text-center py-12 text-muted-foreground">Loading sales summary...</div>}
      </div>
    </DashboardLayout>
  );
}
